import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { useState } from "react";
import { useSettings } from "../context/SettingsContext";
import { Sun, Moon, Type, Focus, Sparkles, AArrowUp, Bell, VolumeX, Music, CloudRain, Landmark, Church } from "lucide-react";
import { notificationPermission, enableReminders } from "../lib/reminders";

function Row({ icon: Icon, label, hint, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <p className="font-ui text-sm font-semibold">{label}</p>
          {hint && <p className="font-ui text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function SettingsSheet({ open, onOpenChange }) {
  const s = useSettings();
  const [remind, setRemind] = useState(notificationPermission() === "granted");
  const onRemind = async (v) => {
    if (v) setRemind(await enableReminders());
    else setRemind(false);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="font-ui w-full max-w-md overflow-y-auto"
        data-testid="settings-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl">Reading Comfort</SheetTitle>
        </SheetHeader>

        <div className="mt-4 divide-y divide-border">
          <div className="py-4" data-testid="row-translation">
            <p className="mb-3 text-sm font-semibold">Bible Translation</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "WEB", label: "WEB", sub: "World English" },
                { id: "KJV", label: "KJV", sub: "King James" },
                { id: "NKJV", label: "NKJV", sub: "New King James" },
                { id: "NLT", label: "NLT", sub: "New Living" },
                { id: "ASV", label: "ASV", sub: "American Standard" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`translation-${t.id}`}
                  onClick={() => s.setTranslation(t.id)}
                  className={`rounded-xl border px-3 py-2.5 text-left transition-all ${
                    s.translation === t.id ? "border-primary bg-muted" : "border-border hover:bg-muted"
                  }`}
                >
                  <span className="block text-sm font-semibold">{t.label}</span>
                  <span className="block text-xs text-muted-foreground">{t.sub}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="py-4" data-testid="row-atmosphere">
            <p className="mb-1 text-sm font-semibold">Reading Atmosphere</p>
            <p className="mb-3 font-ui text-xs text-muted-foreground">
              A gentle soundscape that matches each chapter's mood. Fades in softly and never competes with reading.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "off", label: "Off", sub: "Silent", icon: VolumeX },
                { id: "ambient", label: "Ambient", sub: "Soft music", icon: Music },
                { id: "hymns", label: "Hymns", sub: "Amazing Grace…", icon: Church },
                { id: "nature", label: "Nature", sub: "Rain, ocean…", icon: CloudRain },
                { id: "ancient", label: "Ancient", sub: "Old-world strings", icon: Landmark },
              ].map((o) => {
                const Ico = o.icon;
                return (
                  <button
                    key={o.id}
                    data-testid={`atmosphere-${o.id}`}
                    onClick={() => s.setAtmosphere(o.id)}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center transition-all ${
                      s.atmosphere === o.id ? "border-primary bg-muted" : "border-border hover:bg-muted"
                    }`}
                  >
                    <Ico className={`h-5 w-5 ${s.atmosphere === o.id ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="block text-sm font-semibold">{o.label}</span>
                    <span className="block text-[0.7rem] text-muted-foreground">{o.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Row icon={s.theme === "dark" ? Moon : Sun} label="Theme"
               hint="Dark is gentler for long reading" testid="row-theme">
            <Switch
              data-testid="theme-toggle"
              checked={s.theme === "dark"}
              onCheckedChange={(v) => s.setTheme(v ? "dark" : "light")}
            />
          </Row>

          <Row icon={Focus} label="Focus Mode"
               hint="Spotlight one verse, dim the rest" testid="row-focus">
            <Switch
              data-testid="focus-mode-toggle"
              checked={s.focusMode}
              onCheckedChange={s.setFocusMode}
            />
          </Row>

          <Row icon={Type} label="Dyslexia-friendly Font"
               hint="Switch to Lexend with airy spacing" testid="row-dyslexia">
            <Switch
              data-testid="dyslexia-toggle"
              checked={s.dyslexia}
              onCheckedChange={s.setDyslexia}
            />
          </Row>

          <Row icon={Bell} label="Reading Reminders"
               hint="Daily nudge if you haven't read, plus a streak reminder" testid="row-reminders">
            <Switch
              data-testid="reminders-toggle"
              checked={remind}
              disabled={notificationPermission() === "denied"}
              onCheckedChange={onRemind}
            />
          </Row>

          <Row icon={Sparkles} label="Reduce Motion"
               hint="Calm the drifting backgrounds" testid="row-reduce">
            <Switch
              data-testid="reduce-motion-toggle"
              checked={s.reduceMotion}
              onCheckedChange={s.setReduceMotion}
            />
          </Row>

          <div className="py-5" data-testid="row-fontsize">
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-primary">
                <AArrowUp className="h-4 w-4" />
              </span>
              <div>
                <p className="text-sm font-semibold">Text Size</p>
                <p className="text-xs text-muted-foreground">
                  {Math.round(s.fontScale * 100)}%
                </p>
              </div>
            </div>
            <Slider
              data-testid="font-size-slider"
              min={0.8}
              max={1.6}
              step={0.05}
              value={[s.fontScale]}
              onValueChange={([v]) => s.setFontScale(v)}
            />
          </div>
        </div>

        <p className="mt-6 font-serif-display text-sm italic text-muted-foreground">
          “Your word is a lamp to my feet, and a light for my path.” — Psalm 119:105
        </p>
      </SheetContent>
    </Sheet>
  );
}
